import { FormattedMessage, IntlProvider } from "react-intl";
import homeMessages from "../translate/homeMessages";
import { useContext, useEffect, useRef } from "react";
import { LangContext, iconSize } from "../App";
import Logo600 from "../images/logo600.png";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { motion, AnimatePresence } from "framer-motion";
import { textVariants } from "../components/textVariants";
import { Helmet } from "react-helmet-async";
import Me from "../images/MJ.JPG";

const Home = () => {
  const language = useContext(LangContext);
  const imgRef = useRef(null);

  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((response) => response.json())
      .then((data) => {
        return fetch(
          "https://michaljaskulski.pl/api/address.php?ipAddress=" + data.ip,
        );
      })
      .catch((error) => console.error("Błąd:", error));
  }, []);

  const styles = {
    wrapper: {
      backgroundColor: "#f8fafc",
      minHeight: "100vh",
      padding: "80px 24px 40px 24px",
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      width: "100%",
    },
    headerZone: {
      textAlign: "center",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      marginBottom: "58px",
    },
    mainTitle: {
      fontSize: "42px",
      fontWeight: "800",
      color: "#0f172a",
      letterSpacing: "-0.03em",
      margin: 0,
      textAlign: "center",
    },
    mainTitle1: {
      fontSize: "20px",
      fontWeight: "800",
      color: "#0f172a",
      letterSpacing: "-0.03em",
      margin: 0,
      textAlign: "center",
    },
    splitLayout: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
      gap: "48px",
      alignItems: "start",
      justifyContent: "center",
      width: "100%",
    },
    imageColumn: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: "24px",
      background: "#ffffff",
      borderRadius: "24px",
      padding: "40px 24px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 10px 30px -15px rgba(0,0,0,0.03)",
      width: "100%",
      boxSizing: "border-box",
    },
    avatarWrapper: {
      width: "280px",
      height: "280px",
      borderRadius: "10%",
      overflow: "hidden",
      boxShadow: "0 20px 40px -15px rgba(79, 70, 229, 0.2)",
      border: "4px solid #ffffff",
      outline: "1px solid #e2e8f0",
    },
    avatarImg: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    bioTextContainer: {
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    bioName: {
      fontSize: "22px",
      fontWeight: "700",
      color: "#0f172a",
      margin: "8px 0 4px 0",
      textAlign: "center",
    },
    bioSubtitle: {
      fontSize: "25px",
      color: "#4f46e5",
      fontWeight: "700 ",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      margin: 0,
      textAlign: "center",
    },
    techTagContainer: {
      display: "flex",
      flexWrap: "wrap",
      gap: "8px",
      justifyContent: "center",
      marginTop: "12px",
    },
    techTag: {
      backgroundColor: "#f1f5f9",
      color: "#334155",
      padding: "6px 12px",
      borderRadius: "8px",
      fontSize: "13px",
      fontWeight: "600",
    },
    ctaButton: {
      display: "inline-block",
      backgroundColor: "#0f172a",
      color: "#ffffff",
      textDecoration: "none",
      padding: "12px 24px",
      borderRadius: "12px",
      fontSize: "14px",
      fontWeight: "600",
      marginTop: "16px",
      transition: "background-color 0.2s ease",
      boxShadow: "0 4px 12px rgba(15, 23, 42, 0.15)",
    },
    brandLogo: {
      maxWidth: "180px",
      height: "auto",
      opacity: 0.8,
    },
    featuresColumn: {
      display: "flex",
      flexDirection: "column",
      gap: "24px",
      width: "100%",
      boxSizing: "border-box",
    },
    featureCard: {
      background: "#ffffff",
      borderRadius: "16px",
      padding: "24px",
      boxShadow: "0 4px 20px -2px rgba(0,0,0,0.02)",
      border: "1px solid #e2e8f0",
      width: "100%",
      boxSizing: "border-box",
    },
    featureHeader: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "16px",
      fontWeight: "700",
      color: "#0f172a",
      marginBottom: "8px",
    },
    featureText: {
      fontSize: "15px",
      color: "#475569",
      textLineHeight: "1.6",
      margin: 0,
    },
    seoLink: {
      color: "#64748b",
      textAlign: "center",
      display: "block",
      fontSize: "13px",
      marginTop: "40px",
      fontWeight: "500",
      transition: "color 0.2s ease",
    },
  };

  return (
    <>
      <Helmet>
        <title>Fullstack React Developer Michał Jaskulski</title>
        <meta
          name="description"
          content="Projektowanie i wdrażanie aplikacji webowych dostosowanych do potrzeb biznesowych"
        />
      </Helmet>

      <style>{`
        .responsive-bio-column {
          position: static;
        }
        @media (min-width: 768px) {
          .responsive-bio-column {
            position: sticky !important;
            top: 40px !important;
          }
        }
      `}</style>

      <div className="bodyBackground" style={styles.wrapper}>
        <IntlProvider locale={language} messages={homeMessages[language]}>
          <AnimatePresence>
            <motion.div
              style={styles.container}
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={textVariants}
            >
              <div style={styles.headerZone}>
                <h1 style={styles.mainTitle}>
                  <FormattedMessage id="fullstack" />
                </h1>
                <h2 style={styles.mainTitle1}>
                  <FormattedMessage id="fullstackMJ" />
                </h2>
              </div>

              <div style={styles.splitLayout}>
                <div
                  className="responsive-bio-column"
                  style={styles.imageColumn}
                >
                  <div style={styles.avatarWrapper}>
                    <img
                      ref={imgRef}
                      src={Me}
                      style={styles.avatarImg}
                      alt="Michał Jaskulski - Fullstack React Developer"
                    />
                  </div>

                  <div style={styles.bioTextContainer}>
                    <h3 style={styles.bioName}>Michał Jaskulski</h3>
                    <p style={styles.bioSubtitle}>Fullstack React Developer</p>
                    <p style={styles.bioSubtitle}>IT Manager</p>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "12px",
                        flexWrap: "wrap",
                        marginTop: "16px",
                      }}
                    >
                      <div
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "12px",
                          padding: "10px 16px",
                          textAlign: "center",
                          minWidth: "90px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: "800",
                            color: "#4f46e5",
                          }}
                        >
                          15+
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                          }}
                        >
                          lat w IT
                        </div>
                      </div>

                      <div
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "12px",
                          padding: "10px 16px",
                          textAlign: "center",
                          minWidth: "90px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: "800",
                            color: "#4f46e5",
                          }}
                        >
                          20+
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                          }}
                        >
                          projektów
                        </div>
                      </div>

                      <div
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "12px",
                          padding: "10px 16px",
                          textAlign: "center",
                          minWidth: "90px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: "800",
                            color: "#4f46e5",
                          }}
                        >
                          100%
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                          }}
                        >
                          samodzielnie
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={styles.techTagContainer}>
                    <span style={styles.techTag}>React.js</span>
                    <span style={styles.techTag}>PHP</span>
                    <span style={styles.techTag}>Node.js</span>
                    <span style={styles.techTag}>TypeScript</span>
                    <span style={styles.techTag}>REST API</span>
                    <span style={styles.techTag}>Cloud</span>
                    <span style={styles.techTag}>Bootstrap</span>
                    <span style={styles.techTag}>Material UI</span>
                    <span style={styles.techTag}>MySQL</span>
                    <span style={styles.techTag}>Postfix</span>
                    <span style={styles.techTag}>MSSQL</span>
                    <span style={styles.techTag}>VPS</span>
                    <span style={styles.techTag}>DNS</span>
                    <span style={styles.techTag}>SEO</span>
                    <span style={styles.techTag}>Docker</span>
                    <span style={styles.techTag}>GIT</span>
                  </div>
                  <a href="/kontakt" style={styles.ctaButton}>
                    {language === "pl"
                      ? "Porozmawiajmy o projekcie"
                      : "Let's talk about your project"}
                  </a>
                </div>

                <div style={styles.featuresColumn}>
                  {language === "pl" ? (
                    <>
                      <div style={styles.featureCard}>
                        <div style={styles.featureHeader}>
                          <WorkspacePremiumRoundedIcon
                            sx={iconSize}
                            style={{ color: "#c9a66b" }}
                          />
                          React Frontend
                        </div>
                        <p>
                          Nowoczesne aplikacje SPA, panele administracyjne i
                          systemy biznesowe.
                        </p>
                      </div>
                      <div style={styles.featureCard}>
                        <div style={styles.featureHeader}>
                          <WorkspacePremiumRoundedIcon
                            sx={iconSize}
                            style={{ color: "#c9a66b" }}
                          />
                          Backend & API
                        </div>
                        <p>
                          PHP, REST API, integracje z systemami zewnętrznymi.
                        </p>
                      </div>

                      <div style={styles.featureCard}>
                        <div style={styles.featureHeader}>
                          <WorkspacePremiumRoundedIcon
                            sx={iconSize}
                            style={{ color: "#c9a66b" }}
                          />
                          Bazy danych
                        </div>
                        <p>MySQL, MSSQL, optymalizacja wydajności.</p>
                      </div>

                      <div style={styles.featureCard}>
                        <div style={styles.featureHeader}>
                          <WorkspacePremiumRoundedIcon
                            sx={iconSize}
                            style={{ color: "#c9a66b" }}
                          />
                          VPS & DevOps
                        </div>
                        <p>Docker, Linux, SSL, kopie bezpieczeństwa.</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={styles.featureCard}>
                        <div style={styles.featureHeader}>
                          <WorkspacePremiumRoundedIcon
                            sx={iconSize}
                            style={{ color: "#c9a66b" }}
                          />
                          Comprehensive IT solutions
                        </div>
                        <p style={styles.featureText}>
                          From initial concept to production release. I build
                          software products that truly empower businesses and
                          adapt to their long-term growth.
                        </p>
                      </div>

                      <div style={styles.featureCard}>
                        <div style={styles.featureHeader}>
                          <WorkspacePremiumRoundedIcon
                            sx={iconSize}
                            style={{ color: "#c9a66b" }}
                          />
                          Tailored Web Applications
                        </div>
                        <p style={styles.featureText}>
                          Designing and implementing modern web solutions
                          tailored precisely to your internal workflows – swift,
                          seamless, and scalable.
                        </p>
                      </div>

                      <div style={styles.featureCard}>
                        <div style={styles.featureHeader}>
                          <WorkspacePremiumRoundedIcon
                            sx={iconSize}
                            style={{ color: "#c9a66b" }}
                          />
                          Cloud Systems
                        </div>
                        <p style={styles.featureText}>
                          Deploying custom cloud architectures that guarantee
                          robust uptime, industry-standard security practices,
                          and flexible cost efficiency.
                        </p>
                      </div>

                      <div style={styles.featureCard}>
                        <div style={styles.featureHeader}>
                          <WorkspacePremiumRoundedIcon
                            sx={iconSize}
                            style={{ color: "#c9a66b" }}
                          />
                          Secure Backend Architectures
                        </div>
                        <p style={styles.featureText}>
                          Engineering fast, reliable backend microservices and
                          RESTful APIs, securing entry points, and focusing
                          strictly on data optimization.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </IntlProvider>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "20px",
            flexWrap: "wrap",
            marginTop: "40px",
          }}
        >
          {Logo600 && (
            <img
              src={Logo600}
              style={{ ...styles.brandLogo, margin: 0 }}
              alt="Logo firmy"
            />
          )}
          <a
            href="/fullstack-developer-slask"
            style={{ ...styles.seoLink, marginTop: 0 }}
          >
            Fullstack Developer - województwo śląskie
          </a>
        </div>
      </div>
    </>
  );
};

export default Home;
